import React from 'react';

/**
 * Chunk Retry Utility
 * Handles ChunkLoadError by implementing automatic retry mechanism
 */

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

/**
 * Retry function for failed dynamic imports
 * @param {Function} importFn - The import function to retry
 * @param {number} retries - Number of retries remaining
 * @returns {Promise} - The import promise
 */
export const retryChunkLoad = (importFn, retries = MAX_RETRIES) => {
  return new Promise((resolve, reject) => {
    importFn()
      .then(resolve)
      .catch((error) => {
        // Check if this is a ChunkLoadError
        if (error.name === 'ChunkLoadError' && retries > 0) {
          console.warn(`ChunkLoadError detected. Retrying... (${retries} attempts left)`);
          
          // Wait before retrying
          setTimeout(() => {
            retryChunkLoad(importFn, retries - 1)
              .then(resolve)
              .catch(reject);
          }, RETRY_DELAY);
        } else {
          // If not a chunk error or no retries left, reject
          console.error('Chunk loading failed permanently:', error);
          reject(error);
        }
      });
  });
};

/**
 * Enhanced React.lazy wrapper with retry mechanism
 * @param {Function} importFn - The dynamic import function
 * @returns {React.LazyExoticComponent} - Lazy component with retry capability
 */
export const lazyWithRetry = (importFn) => {
  return React.lazy(() => retryChunkLoad(importFn));
};

/**
 * Global error handler for ChunkLoadError
 * This can be used to handle chunk errors globally
 */
export const setupGlobalChunkErrorHandler = () => {
  const reloadOnceForChunkError = (reason) => {
    try {
      const reloadKey = '__chunk_reload_attempted__';
      if (sessionStorage.getItem(reloadKey) === '1') return false;
      sessionStorage.setItem(reloadKey, '1');
      console.warn('Chunk error detected, forcing a single hard reload:', reason);

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('__chunk_retry__', Date.now().toString());
      window.location.replace(currentUrl.toString());
      return true;
    } catch (error) {
      console.warn('Chunk reload guard failed:', error);
      return false;
    }
  };

  window.addEventListener('error', (event) => {
    if (
      (event.error && event.error.name === 'ChunkLoadError') ||
      String(event.message || '').includes('Loading chunk')
    ) {
      console.warn('Global ChunkLoadError detected:', event.error);
      reloadOnceForChunkError(event.error || event.message);
    }
  });


  // Handle unhandled promise rejections for chunk errors
  window.addEventListener('unhandledrejection', (event) => {
    if (
      (event.reason && event.reason.name === 'ChunkLoadError') ||
      String(event.reason?.message || event.reason || '').includes('Loading chunk')
    ) {
      console.warn('Unhandled ChunkLoadError:', event.reason);
      reloadOnceForChunkError(event.reason);
      event.preventDefault(); // Prevent the default unhandled rejection behavior
    }
  });
};

/**
 * Preload critical chunks to avoid loading issues
 * @param {Array} chunkNames - Array of chunk names to preload
 */
export const preloadCriticalChunks = (chunkNames = []) => {
  if (!chunkNames.length) return;

  chunkNames.forEach(chunkName => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'script';
    link.href = `${process.env.PUBLIC_URL}/static/js/${chunkName}.chunk.js`;
    link.onload = () => console.log(`Preloaded chunk: ${chunkName}`);
    link.onerror = () => console.warn(`Failed to preload chunk: ${chunkName}`);
    document.head.appendChild(link);
  });
};

const ChunkRetryUtils = {
  retryChunkLoad,
  lazyWithRetry,
  setupGlobalChunkErrorHandler,
  preloadCriticalChunks,
};
export default ChunkRetryUtils;
